import React, { useMemo } from 'react';

import { Flex, Form, Input, Modal, Space } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { Select, Text } from 'scouting-ui/lib';

import type { MBCYouthAssignationPayload } from '@appTypes/esb';
import type { AssigYouthToMbcError, ESBBaseError } from '@appTypes/esb/errors';
import { useAssignMBCToYouthMutation } from '@modules/meritBadgeCounselors/api';
import { useMeritBadgeCounselorProfile } from '@modules/meritBadgeCounselors/hooks';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';

import MBImage from './MBImage';

interface Props {
  mbcMemberId: string | number;
  showModal: boolean;
  onClose: VoidFunction;
}

interface addForm {
  LastName: string;
  MemberId: number;
  MbcIds: number[];
}

enum FormFields {
  LastName = 'LastName',
  MemberId = 'MemberId',
  MbcIds = 'MbcIds',
}

export const AssignYouthModal: React.FC<Props> = ({
  mbcMemberId,
  showModal,
  onClose,
}) => {
  const [form] = Form.useForm<addForm>();
  const [assignToYouth, { isLoading: isSaving }] =
    useAssignMBCToYouthMutation();
  const { meritBadges, loading: loadingProfile } =
    useMeritBadgeCounselorProfile({
      memberId: mbcMemberId as string,
      extendedInfo: true,
    });

  const handleClickSave = async () => {
    try {
      const valuesSubmitted = await form.validateFields();

      try {
        const formattedMbIds = valuesSubmitted.MbcIds.map(mbId => ({
          meritBadgeId: mbId,
        }));
        const payload: MBCYouthAssignationPayload[] = [
          {
            counselorMemberId: +mbcMemberId,
            youths: [
              {
                youthMemberId: +valuesSubmitted.MemberId,
                youthLastName: valuesSubmitted.LastName,
                meritBadgesCounseling: formattedMbIds,
              },
            ],
          },
        ];

        await assignToYouth({
          payload,
          invalidateExtra: [
            tagFactory.MeritBadgeCounselorProfile.mbcProfile(+mbcMemberId),
          ],
        }).unwrap();
        toastService.success(
          <FormattedMessage id="mbcDashboard.addYouthModal.success" />,
        );
        onClose();
      } catch (error) {
        const generalError = error as {
          errorCode: string;
          data: AssigYouthToMbcError[] | ESBBaseError;
        };
        if (Array.isArray(generalError.data)) {
          const typedError = error as {
            errorCode: string;
            data: AssigYouthToMbcError[];
          };
          const errorMessage = typedError.data
            .map(({ message }: { message: string }) => message)
            .toString();
          toastService.error(errorMessage);
        } else {
          const typedError = error as { errorCode: string; data: ESBBaseError };
          const errorData = typedError.data;
          const message = errorData.errorDesc.includes(
            'active youth position',
          ) ? (
            <FormattedMessage id="mbcDashboard.addYouthModal.error.noYouthPosition" />
          ) : (
            errorData.errorDesc
          );
          toastService.error(message);
        }
        return;
      }
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const options = useMemo(
    () =>
      meritBadges.map(badge => ({
        label: badge.meritBadgeName,
        value: badge.meritBadgeId,
        image: badge.imageUrl100,
      })),
    [meritBadges],
  );

  return (
    <Modal
      width={800}
      open={showModal}
      destroyOnClose
      title={<FormattedMessage id="mbcDashboard.addYouthModal.title" />}
      cancelText={<FormattedMessage id="shared.cancel" />}
      okText={<FormattedMessage id="shared.save" />}
      onOk={handleClickSave}
      cancelButtonProps={{
        type: 'default',
        onClick: onClose,
        disabled: isSaving,
      }}
      okButtonProps={{ loading: isSaving || loadingProfile }}
      onCancel={onClose}
    >
      <Form clearOnDestroy form={form} initialValues={{}}>
        <Space direction="vertical">
          <Text strong>
            <FormattedMessage id="mbcDashboard.addYouthModal.info" />
          </Text>
          <Form.Item
            name={FormFields.LastName}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ]}
            noStyle
          >
            <Input
              placeholder={intl.formatMessage({
                id: 'shared.personalInfo.lastName',
              })}
              size="small"
            />
          </Form.Item>
          <Form.Item
            name={FormFields.MemberId}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ]}
            noStyle
          >
            <Input
              placeholder={intl.formatMessage({
                id: 'shared.memberId',
              })}
              size="small"
            />
          </Form.Item>
          <Form.Item
            name={FormFields.MbcIds}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ]}
            noStyle
          >
            <Select
              mode="multiple"
              placeholder="Merit badges"
              filterOption={(input, option) =>
                (option?.label ? `${option.label}` : '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={options}
              optionRender={option => (
                <Flex align="center" gap={4}>
                  <MBImage src={option.data.image} height="auto" width={24} />
                  {option.data.label}
                </Flex>
              )}
            />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
};
